import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import {
  CameraOutlined,
  ArrowLeftOutlined,
  MoneyCollectOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  QrcodeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  Table,
  Spin,
  Empty,
  message,
  Image,
  Card,
  Typography,
  Button,
  Tag,
  Row,
  Col,
  Descriptions,
  Input
} from 'antd';
import axiosInstance from '../../utils/axios';

const { Title, Text } = Typography;

const TicketSnapshotsDetail = () => {
  const { showId, dateId, timeslotId } = useParams();
  const navigate = useNavigate();

  const [snapshots, setSnapshots] = useState([]);
  const [showInfo, setShowInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchSnapshots(pagination.current, search);
  }, [showId, dateId, timeslotId, pagination.current, search]);

  const fetchSnapshots = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      let url = `booking/theatre-ticket-snapshots/${showId}/${dateId}/${timeslotId}/`;
      const params = { page, page_size: pagination.pageSize };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axiosInstance.get(url, { params });

      const data = response.data;
      const results = data.results || [];

      if (results.length > 0) {
        const first = results[0];
        setShowInfo({
          movie: first.movie_name,
          thumbnail: first.thumbnail,
          theatre: first.theatre_name,
          screen: first.screen_name,
          date: first.show_date,
          time: first.show_time,
        });
      }

      setSnapshots(results);
      setPagination({
        current: page,
        pageSize: pagination.pageSize,
        total: data.count || 0,
      });
    } catch (error) {
      message.error('Failed to load ticket snapshots');
      console.error(error);
      setSnapshots([]);
      setShowInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || 10,
    }));
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getMediaPath = (fullUrl) => {
    if (!fullUrl) return '';
    try {
      const url = new URL(fullUrl);
      const index = url.pathname.indexOf('mediafiles');
      return index !== -1 ? url.pathname.substring(index) : url.pathname;
    } catch (e) {
      const index = fullUrl.indexOf('mediafiles');
      return index !== -1 ? fullUrl.substring(index) : fullUrl;
    }
  };

  const columns = [
    {
      title: 'QR Code',
      key: 'qr_code',
      align: 'center',
      render: (_, record) => {
        const qrSrc = getMediaPath(record.qr_code);
        return (
          <div style={{ padding: 8 }}>
            <Image
              src={`${process.env.REACT_APP_MEDIA_URL}${qrSrc}`}
              alt="QR Code"
              width={120}
              height={120}
              style={{ border: '1px solid #ddd', borderRadius: 8 }}
              preview={{ mask: <QrcodeOutlined style={{ fontSize: 20 }} /> }}
            />
          </div>
        );
      },
    },
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) => (
        <div>
          <div>
            <UserOutlined style={{ marginRight: 6 }} />
            <strong>{record.booked_by}</strong>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.booked_by_email || record.booked_by_phone || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Booking ID',
      dataIndex: 'booking_id',
      key: 'booking_id',
      render: (id) => <Tag color="purple">{id}</Tag>
    },
    {
      title: 'Seats Booked',
      key: 'seats',
      render: (_, record) => (
        <Tag color="blue">
          {record.seats.length} seat{record.seats.length > 1 ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Seat Numbers',
      key: 'seat_list',
      render: (_, record) => (
        <div style={{ fontSize: 13 }}>
          {record.seats.map((seat, idx) => (
            <Tag key={idx} style={{ margin: '2px 4px' }}>
              {seat.row}{seat.number}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Paid',
      key: 'paid',
      align: 'center',
      render: (_, record) => (
        <Tag
          color={record.paid ? 'green' : 'orange'}
          icon={record.paid ? <CheckCircleOutlined /> : null}
        >
          {record.paid ? 'Paid' : 'Unpaid'}
        </Tag>
      ),
    },
    {
      title: 'QR Verified',
      key: 'verified',
      align: 'center',
      render: (_, record) => (
        <Tag color={record.is_verified ? 'green' : 'default'}>
          {record.is_verified ? 'Verified' : 'Not Verified'}
        </Tag>
      ),
    },
    {
      title: 'Booked At',
      key: 'created_at',
      render: (_, record) => (
        <Text type="secondary">
          {new Date(record.created_at).toLocaleString('en-IN')}
        </Text>
      ),
    },
  ];

  if (loading && snapshots.length === 0) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div className="dashboard">
      <PageHeading name="Ticket Snapshots" icon={<CameraOutlined />} />

      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/theater/shows')}
        style={{ marginBottom: 16 }}
      >
        Back to Show List
      </Button>

      {/* Show Summary */}
      {showInfo && (
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={8}>
            <Card bordered={false}>
              <Image
                src={`${process.env.REACT_APP_MEDIA_URL}${getMediaPath(showInfo.thumbnail)}`}
                alt={showInfo.movie}
                width="100%"
                style={{ borderRadius: 8, maxHeight: 400, objectFit: 'cover' }}
                fallback="https://via.placeholder.com/300x450?text=No+Poster"
                preview={true}
              />
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card title="Show Details" bordered={false}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Movie">
                  <strong>{showInfo.movie}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Theatre">
                  {showInfo.theatre}
                </Descriptions.Item>
                <Descriptions.Item label="Screen">
                  {showInfo.screen}
                </Descriptions.Item>
                <Descriptions.Item label={<><CalendarOutlined /> Date</>}>
                  {new Date(showInfo.date).toLocaleDateString('en-IN')}
                </Descriptions.Item>
                <Descriptions.Item label={<><ClockCircleOutlined /> Show Time</>}>
                  {showInfo.time}
                </Descriptions.Item>
                <Descriptions.Item label={<><MoneyCollectOutlined /> Total Tickets</>}>
                  <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                    {pagination.total}
                  </Title>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}

      {/* Search Bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <Input
          placeholder="Search by Booking ID, Name, Email, Phone or Seat Number"
          prefix={<SearchOutlined />}
          style={{ width: '320px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
        />

        <Text type="secondary">
          Total: <strong>{pagination.total}</strong> ticket{pagination.total !== 1 ? 's' : ''}
        </Text>
      </div>

      {snapshots.length === 0 ? (
        <Empty description="No ticket snapshots available for this show time" />
      ) : (
        <>
          <Table
            dataSource={snapshots}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tickets`,
              onChange: handlePageChange,
              onShowSizeChange: (current, size) => handlePageChange(current, size),
            }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No tickets found' }}
          />
        </>
      )}
    </div>
  );
};

export default TicketSnapshotsDetail;